import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';
import { usePageTitle } from './hooks/usePageTitle';

// Pages
import LoginPage from './pages/LoginPage';
import RoleSelection from './pages/RoleSelection';
import Dashboard from './pages/Dashboard';
import AttendancePage from './pages/AttendancePage';
import StudentsPage from './pages/StudentsPage';
import ReportsPage from './pages/ReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext(null);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Component to handle page titles
function PageTitleHandler() {
  usePageTitle();
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(localStorage.getItem('session_token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setSessionToken(token);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('session_token');
      setUser(null);
      setSessionToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('session_token', token);
    setSessionToken(token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('session_token');
      setSessionToken(null);
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, sessionToken, login, logout, updateUser }}>
      <BrowserRouter>
        <PageTitleHandler />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={!user ? <LoginPage /> : user.role ? <Navigate to="/dashboard" /> : <Navigate to="/role-selection" />} />
          <Route path="/role-selection" element={user && !user.role ? <RoleSelection /> : user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} />
          <Route path="/dashboard" element={user && user.role ? <Dashboard /> : user ? <Navigate to="/role-selection" /> : <Navigate to="/login" />} />
          <Route path="/attendance" element={user && user.role ? <AttendancePage /> : user ? <Navigate to="/role-selection" /> : <Navigate to="/login" />} />
          <Route path="/students" element={user && user.role ? <StudentsPage /> : user ? <Navigate to="/role-selection" /> : <Navigate to="/login" />} />
          <Route path="/reports" element={user && user.role ? <ReportsPage /> : user ? <Navigate to="/role-selection" /> : <Navigate to="/login" />} />
          <Route path="/notifications" element={user && user.role ? <NotificationsPage /> : user ? <Navigate to="/role-selection" /> : <Navigate to="/login" />} />
          <Route path="/settings" element={user && user.role ? <SettingsPage /> : user ? <Navigate to="/role-selection" /> : <Navigate to="/login" />} />
          <Route path="/" element={<Navigate to={user ? (user.role ? "/dashboard" : "/role-selection") : "/login"} />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

import React from 'react';
export default App;